<template>
  <div v-if="courseId">
    <AlertDismiss ref="alert" />
    <form @submit.prevent="onSubmit">
      <div class="row">
        <div class="col-md-8">
          <label for="nameInput">
            Nombre
          </label>
          <input v-model="form.courseName" class="form-control" placeholder="Ingrese el nombre del tema" required>
        </div>
      </div>

      <hr>

      <ListEdit v-model="resources" @handleAdd="addResource" @handleRemove="removeResource" label="Recursos">
        <template #default="{ index }">
          <div class="row d-flex align-items-center">
            <div class="col-auto flex-shrink-0 pe-0">
              <MaterialIcon :file="resources[index].file"/>
            </div>
            <div class="col-sm">
              <FileInput
                :value="resources[index].file"
                @input="(value) => handleFileInput(index, value)"
                :ref="'fileInput' + index"
                class="form-control form-control-sm"
                accept=".pdf, .mp4, .avi, .mkv, .webm, .ogg, .mov"
              />
            </div>
          </div>
        </template>
      </ListEdit>

      <hr>
      <ListEdit v-model="assesments" @handleAdd="addAssesment" @handleRemove="removeAssesment" label="Evaluaciones">
        <template #default="{ index }">
          <div class="row d-flex align-items-center">
            <div class="col-sm pe-0">
              <input v-model="assesments[index].name" :placeholder="'Nombre evaluación ' + (index + 1)" type="text"
                class="form-control form-control-sm" required>
            </div>

            <div class="col-auto flex-shrink-0">
              <b-button size="sm" variant="light" @click="handleEdit(index)" tabindex="-1"
                :title="iconProps(index).title" v-b-tooltip.hover>
                <b-icon :icon="iconProps(index).name" :variant="iconProps(index).variant"></b-icon>
              </b-button>
            </div>
          </div>
        </template>
      </ListEdit>

      <div class="d-flex row align-items-center mt-3">
        <div class="col-auto">
          <button type="submit" class="btn btn-primary" :disabled="isUploading">
            <b-spinner small v-if="isUploading"></b-spinner>
            Crear tema
          </button>
        </div>
        <div class="col-sm flex-grow-1">
          <ProgressCustom ref="progress" v-model="isUploading" />
        </div>
      </div>
    </form>

    <BaseModal v-model="editModalShow" size="lg">
      <CurriculumAssesmentForm v-if="inEditionIndex !== null" v-model="inEditionAssesment.questions" />
    </BaseModal>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import AlertDismiss from '@/components/shared/Alert-dismiss.vue'
import ListEdit from '@/components/shared/List-edit.vue'
import FileInput from '@/components/shared/File-input.vue'
import BaseModal from '@/components/shared/Base-modal.vue';
import CurriculumAssesmentForm from './CurriculumForm/Curriculum-assesment-form.vue'
import { EventNames } from '@/plugins/enums/index.js'
import ProgressCustom from '@/components/shared/Progress-custom.vue';
import MaterialIcon from './CurriculumForm/Material-icon.vue';

export default {
  name: 'CurriculumForm',

  props: {
    courseId: String,
  },

  components: {
    AlertDismiss,
    ListEdit,
    FileInput,
    BaseModal,
    CurriculumAssesmentForm,
    ProgressCustom,
    MaterialIcon
  },

  data() {
    return {
      form: {
        courseName: null
      },
      resources: [],
      assesments: [],
      inEditionIndex: null,
      inEditionBackup: null,

      isUploading: false,
      editModalShow: false,
      allowUpdateFlag: false, // to bypass the watcher onthe modal an avoid restoring the backup
    }
  },

  computed: {
    inEditionAssesment() {
      // we are doing using v-model on the assesment form so this always updates back
      //we also keep a bakcup of the assesment being evaluated and rollbck unless approved with a safe button
      if (this.inEditionIndex === null || this.assesments.length === 0) {
        return null;
      }
      return this.assesments[this.inEditionIndex]
    },
  },

  mounted() {
    this.addResource()
    this.addAssesment()

    this.$eventBus.$on(EventNames.SaveChangesCurriculumAssesment, () => {
      this.allowUpdateFlag = true
      this.editModalShow = false
    })
  },

  methods: {
    ...mapActions(["saveTheme", "saveThemeResourceDocument"]),

    async onSubmit() {
      
      this.isUploading = true
      let result = await this.saveAllResources()
      if (!result) {
        this.resetSubmitProcess()
        return
      }

      let resultTheme = await this.saveCurriculum()
      if (!resultTheme) {
        this.resetSubmitProcess()
        return
      }

      this.setProgress(100, "Completado")
      this.isUploading = false
      setTimeout(() => {
        this.setProgress(0)
      }, 1000)
      this.$refs.alert.showAlert('Tema creado correctamente', 'success')
      this.reset();
    },

    // lets try serial execution
    async saveAllResources() {
      for (let [index, resource] of Object.entries(this.resources)) {
        if (resource.file === null || resource.route) {
          continue
        }

        this.setProgress(
          80 / this.resources.length,
          `Subidos ${index} de ${this.resources.length} recursos`        )

        let result = await this.saveResource(resource.file)
        if (result === null) {
          return false
        }

        this.$set(this.resources, index, {...resource, route: result})
      }
      return true
    },

    async saveCurriculum() {
      let form = {}
      form.name = this.form.courseName
      form.courseId = this.courseId
      form.materials = this.resources.map(r => {
        return {
          name: r.file.name,
          route: r.route
        }
      })

      form.evaluations = this.assesments.map(a => {
        let questions = a.questions.map(q => {
          let answers = q.options.map(o => {
            return { correctAnswer: o.isCorrect, text: o.label }
          })

          return { text: q.description, answers }
        })

        return { name: a.name, questions }
      })

      this.setProgress(90, 'Cargando evaluaciones...')
      let result = await this.saveTheme(form)

      if (result.isError) {
        this.$refs.alert.showAlert(result.message, 'warning')
        return false  
      }
      this.$eventBus.$emit(EventNames.UpdateCurriculumList, this.courseId)

      return true
    },

    async saveResource(file) {
      let result = await this.saveThemeResourceDocument(file)
      if (result.isError) {
        this.$refs.alert.showAlert(result.message, 'warning')
        return null
      }

      return result.data
    },

    reset() {
      this.form.courseName = null
      for (let i = 0; i <= this.resources.length - 1; i++) {
        this.$refs["fileInput" + i].reset()
      }
      this.resources = []
      this.assesments = []
      this.inEditionIndex = null
      this.inEditionBackup = null
      this.addResource()
      this.addAssesment()
    },

    addResource() {
      this.resources.push({
        file: null,
        route: null
      })
    },

    removeResource(index) {
      this.resources.splice(index, 1)
    },

    addAssesment() {
      this.assesments.push({
        name: null,
        questions: [],
      })
    },

    removeAssesment(index) {
      this.assesments.splice(index, 1)
      this.inEditionIndex = null
    },

    handleFileInput(index, value) {
      this.$set(this.resources, index, {
        file: value,
        route: null
      })
    },

    handleEdit(index) {
      this.editModalShow = true
      this.inEditionIndex = index
      this.inEditionBackup = JSON.parse(JSON.stringify(this.inEditionAssesment))
    },

    hasQuestions(index) {
      return this.assesments[index].questions.length !== 0
    },

    iconProps(index) {
      let saved = this.hasQuestions(index) && !this.editModalShow
      return {
        name: saved ? 'pencil-square' : 'journal-plus',
        variant: saved ? 'success' : 'primary',
        title: saved ? 'Editar preguntas' : 'Crear preguntas',
      }
    },

    setProgress(value, message = '') {
      this.progress = value;
      this.$refs.progress.progress(value, message);
    },

    resetSubmitProcess() {
      this.setProgress(0);
      this.isUploading = false;
    },

  },

  watch: {
    editModalShow(val) {
      if (val) {
        return
      }

      if (this.allowUpdateFlag) {
        // restore the flag model is being alreay updated
        this.allowUpdateFlag = false
        return
      }

      this.$set(this.assesments, this.inEditionIndex, this.inEditionBackup)
    }
  }
}
</script>
<style scoped></style>